import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { CurrencyInfo } from './adapter/MasterDocumentAdapter.types';
import { useMasterDocumentTranslation } from './i18n';

// ─── Props ─────────────────────────────────────────────────

export interface CurrencySelectorModalProps {
  open: boolean;
  /** Código de la moneda actualmente seleccionada */
  currentCode: string;
  /** Lista de monedas disponibles */
  currencies: CurrencyInfo[];
  /** true mientras se carga la lista del backend */
  loading?: boolean;
  /** Callback cuando el usuario confirma el cambio */
  onSelect: (currency: CurrencyInfo) => void;
  onClose: () => void;
}

// ─── CurrencySelectorModal ─────────────────────────────────

/**
 * Modal para cambiar la moneda del documento.
 * Muestra la lista de monedas activas con buscador y selección.
 *
 * @example
 * ```tsx
 * <CurrencySelectorModal
 *   open={modalOpen}
 *   currentCode="COP"
 *   currencies={currencies}
 *   onSelect={(c) => handleChangeCurrency(c.code)}
 *   onClose={() => setModalOpen(false)}
 * />
 * ```
 */
export function CurrencySelectorModal({
  open,
  currentCode,
  currencies,
  loading = false,
  onSelect,
  onClose,
}: CurrencySelectorModalProps) {
  const { t } = useMasterDocumentTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(currentCode);

  // Sync selected with currentCode when modal opens
  useEffect(() => {
    if (open) {
      setSelected(currentCode);
      setSearch('');
    }
  }, [open, currentCode]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const filtered = currencies.filter(
    (c) =>
      c.code.toLowerCase().includes(search.toLowerCase()) ||
      c.name.toLowerCase().includes(search.toLowerCase()),
  );

  function handleConfirm() {
    const currency = currencies.find((c) => c.code === selected);
    if (currency) onSelect(currency);
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={clsx(
          'fixed z-[301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-w-[480px]',
          'bg-bg-primary rounded-2xl',
          'shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
          'flex flex-col',
          'max-h-[90vh]',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="currency-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
          <h2
            id="currency-modal-title"
            className="text-[15px] font-bold text-content-primary dark:text-dark-content-primary"
          >
            {t('master_document.currency.changeTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'p-1.5 rounded-md',
              'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
              'text-content-secondary dark:text-dark-content-secondary',
              'transition-colors duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
            aria-label={t('master_document.chrome.close')}
          >
            <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pb-3 shrink-0">
          <div className="relative">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary dark:text-dark-content-secondary pointer-events-none"
              aria-hidden="true"
            />
            <input
              type="text"
              placeholder={t('master_document.currency.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={clsx(
                'w-full pl-9 pr-3 py-2 text-sm rounded-lg',
                'border border-border-primary dark:border-dark-border-primary',
                'bg-bg-primary dark:bg-dark-bg-secondary',
                'text-content-primary dark:text-dark-content-primary',
                'placeholder:text-content-tertiary dark:placeholder:text-dark-content-secondary',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-400',
                'dark:focus:ring-dark-border-custom',
              )}
              autoFocus
            />
          </div>
        </div>

        {/* Currency list */}
        <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-content-tertiary dark:text-dark-content-secondary py-8">
              {t('master_document.currency.notFound')}
            </p>
          ) : (
            <ul role="listbox" aria-label={t('master_document.currency.listAria')}>
              {filtered.map((currency) => {
                const isSelected = currency.code === selected;
                return (
                  <li key={currency.code} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      onClick={() => setSelected(currency.code)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left',
                        'transition-colors duration-100',
                        isSelected
                          ? 'bg-primary-100 dark:bg-primary-600/15'
                          : 'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                      )}
                    >
                      {/* Code badge */}
                      <span
                        className={clsx(
                          'shrink-0 w-12 text-center text-[12px] font-bold rounded-md px-1.5 py-1',
                          isSelected
                            ? 'bg-primary-200 text-primary-700 dark:bg-primary-600/30 dark:text-primary-300'
                            : 'bg-background-secondary text-content-secondary dark:bg-dark-bg-secondary dark:text-dark-content-secondary',
                        )}
                      >
                        {currency.code}
                      </span>

                      {/* Name */}
                      <span
                        className={clsx(
                          'flex-1 text-sm',
                          isSelected
                            ? 'font-semibold text-primary-700 dark:text-primary-300'
                            : 'text-content-primary dark:text-dark-content-primary',
                        )}
                      >
                        {currency.name}
                      </span>

                      {/* Base badge */}
                      {currency.isBase && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-content-tertiary dark:text-dark-content-secondary border border-border-primary dark:border-dark-border-primary rounded px-1.5 py-0.5 shrink-0">
                          {t('master_document.currency.base')}
                        </span>
                      )}

                      {/* Selected indicator */}
                      {isSelected && (
                        <span className="shrink-0 w-4 h-4 rounded-full bg-primary-600 dark:bg-primary-400 flex items-center justify-center">
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                            <path d="M1.5 4L3.5 6L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border-primary dark:border-dark-border-primary shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-semibold',
              'border border-border-primary text-content-tertiary',
              'hover:bg-background-secondary transition-colors duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:border-dark-border-primary dark:text-dark-content-secondary',
              'dark:hover:bg-dark-bg-secondary',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
          >
            {t('master_document.chrome.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selected === currentCode}
            className={clsx(
              'px-5 py-2 rounded-md text-sm font-semibold',
              'bg-primary-inverse-background text-white',
              'hover:bg-primary-700 transition-colors duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'disabled:opacity-40 disabled:pointer-events-none',
            )}
          >
            {t('master_document.currency.changeTitle')}
          </button>
        </div>
      </div>
    </>
  );
}
